'use client';
import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button, Card, Col, Drawer, Input, message, Modal, Row } from 'antd';
import { FormInstance } from 'antd/es/form';
import { useConnection } from '@sezenta/connection';
import Form, { setValidationErrors } from '@sezenta/antd-schema-form';
import { useEvents } from './EventContext';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type DataEditModalInstance = {
  edit: (
    item: any,
    schema: any,
    title: string,
    url: string,
    prepare?: (record: any) => any,
    onChange?: (changedValues: any, values: any, form: FormInstance) => void,
  ) => Promise<any>;
  create: (
    schema: any,
    title: string,
    url: string,
    prepare?: (record: any) => any,
    onChange?: (changedValues: any, values: any, form: FormInstance) => void,
    defaultValue?: any,
  ) => Promise<any>;
};

type Props = {
  type: string;
  width?: number;
};

const DataEditModalInternal: ForwardRefRenderFunction<
  DataEditModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<any>();
  const [schema, setSchema] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [action, setAction] = useState<string>('EDIT');
  const prepareRef = useRef<(r: any) => any>();
  const onChangeRef =
    useRef<(changedValues: any, values: any, form: FormInstance) => void>();
  const [form] = Form.useForm();
  const connection = useConnection();
  const [valueChangeNotif, setValueChangeNotif] = useState<boolean>(false);
  const events = useEvents();
  const { t } = useTranslation();
  const promiseRef = useRef<{
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }>();
  useImperativeHandle(
    ref,
    () => ({
      edit: (
        item: any,
        schema: any,
        title: string,
        url: string,
        prepare?: (record: any) => any,
        onChange?: (
          changedValues: any,
          values: any,
          form: FormInstance,
        ) => void,
      ) => {
        setLoading(false);
        setSchema(schema);
        setCurrentItem(item);
        form.resetFields();
        form.setFieldsValue(item);
        setTitle(title);
        setUrl(url);
        setAction('UPDATE');
        prepareRef.current = prepare;
        onChangeRef.current = onChange;
        setVisible(true);
        setValueChangeNotif(!valueChangeNotif);
        return new Promise<any>((resolve, reject) => {
          promiseRef.current = { resolve, reject };
        });
      },
      create: (
        schema: any,
        title: string,
        url: string,
        prepare?: (record: any) => any,
        onChange?: (
          changedValues: any,
          values: any,
          form: FormInstance,
        ) => void,
        defaultValue?: any,
      ) => {
        setLoading(false);
        setSchema(schema);
        setCurrentItem(undefined);
        form.resetFields();
        if (defaultValue) {
          form.setFieldsValue(defaultValue);
        }
        setTitle(title);
        setUrl(url);
        prepareRef.current = prepare;
        onChangeRef.current = onChange;
        setAction('CREATE');
        setVisible(true);
        setValueChangeNotif(!valueChangeNotif);
        return new Promise<any>((resolve, reject) => {
          promiseRef.current = { resolve, reject };
        });
      },
    }),
    [form, valueChangeNotif],
  );

  const save = useCallback(
    (values: any) => {
      setLoading(true);
      const updates = {
        ...currentItem,
        ...values,
      };
      delete updates.__v;
      (action === 'CREATE' ? connection.post : connection.patch)(
        url,
        prepareRef.current ? prepareRef.current(updates) : updates,
      )
        .then((res) => {
          setVisible(false);
          events.emit(props.type, { action, data: res.data });
          promiseRef.current?.resolve(res.data);
        })
        .catch((reason) => {
          console.error(reason);
          const data = reason?.response?.data;
          // Server field validation (FormValidationException) → map onto the form.
          // The shared helper double-wraps array messages (errors: [[msg]]) which
          // Ant won't render, so map them ourselves to flat string[] and also show
          // a toast (the field may be scrolled out of view in the drawer).
          const ve = data?.validationErrors as
            | Record<string, string | string[]>
            | undefined;
          if (ve && typeof ve === 'object') {
            form.setFields(
              Object.entries(ve).map(([name, errs]) => ({
                name: name.split('.'),
                errors: (Array.isArray(errs) ? errs : [errs]).map((m) =>
                  String(m),
                ),
              })),
            );
            const first = Object.values(ve)[0];
            const firstMsg = Array.isArray(first) ? first[0] : first;
            if (firstMsg) message.error(String(firstMsg));
            return;
          }
          // Otherwise surface a server guard message (e.g. last-admin lockout) or a
          // class-validator message array, falling back to a generic failure.
          const serverMsg = data?.message;
          message.error(
            (Array.isArray(serverMsg) ? serverMsg[0] : serverMsg) ??
              'Failed to save',
          );
        })
        .finally(() => setLoading(false));
    },
    [
      currentItem,
      action,
      connection.post,
      connection.patch,
      url,
      events,
      props.type,
      form,
    ],
  );

  return (
    <Drawer
      width={props.width ?? 650}
      // confirmLoading={loading}
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      // okText="Save"
      // onOk={form.submit}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={() => setVisible(false)}>Close</Button>
          <Button loading={loading} type="primary" onClick={form.submit}>
            Save
          </Button>
        </div>
      }
    >
      <Form
        valueChangeNotif={valueChangeNotif}
        layout="vertical"
        onValuesChange={(changedValues, values) =>
          onChangeRef.current?.(changedValues, values, form)
        }
        form={form}
        onFinish={save}
      >
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useDataEditModal = () => useRef<DataEditModalInstance>(null);

export const DataEditModal = React.forwardRef<DataEditModalInstance, Props>(
  DataEditModalInternal,
);
